import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, CheckCircle2, Clock, ListChecks, CalendarDays,
  Users, TrendingUp, Activity, Flame, Wallet
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";

const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

function Kpi({ label, value, icon: Icon, tone = "default", testid, to }) {
  const tones = {
    default: "border-border",
    danger: "border-red-500/60",
    warn: "border-amber-500/60",
    ok: "border-emerald-500/60",
    info: "border-blue-500/60",
  };
  const iconColors = {
    default: "text-slate-500", danger: "text-red-500",
    warn: "text-amber-500", ok: "text-emerald-500", info: "text-blue-500",
  };
  const inner = (
    <Card data-testid={testid} className={`p-4 border-l-[3px] ${tones[tone]} bg-card hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] cursor-pointer group`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{label}</div>
          <div className="font-mono font-semibold text-3xl mt-2 tabular-nums group-hover:text-blue-600 transition-colors">{value ?? 0}</div>
        </div>
        <Icon className={`w-4 h-4 ${iconColors[tone]}`} />
      </div>
    </Card>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/billing/summary").then(({ data }) => setBilling(data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-sm text-muted-foreground font-mono">Loading dashboard…</div>;

  const t = stats.totals;
  const deptData = Object.entries(stats.department_pending).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {format(new Date(), "EEEE · dd MMMM yyyy")}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
            Good day, {user?.name?.split(" ")[0]}.
          </h1>
        </div>
        <div className="text-xs text-muted-foreground font-mono">
          {t.total_clients} clients · {t.total_tasks} tasks
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi testid="kpi-overdue" label="Overdue" value={t.overdue} icon={Flame} tone="danger" to="/tasks?view=overdue" />
        <Kpi testid="kpi-due-today" label="Due Today" value={t.due_today} icon={AlertTriangle} tone="warn" to="/tasks?view=due_today" />
        <Kpi testid="kpi-due-tomorrow" label="Due Tomorrow" value={t.due_tomorrow} icon={Clock} tone="warn" to="/tasks?view=due_tomorrow" />
        <Kpi testid="kpi-due-week" label="Due This Week" value={t.due_week} icon={CalendarDays} tone="info" to="/tasks?view=due_week" />
        <Kpi testid="kpi-pending" label="Pending" value={t.pending} icon={ListChecks} to="/tasks?view=pending" />
        <Kpi testid="kpi-in-progress" label="In Progress" value={t.in_progress} icon={Activity} tone="info" to="/tasks?status=In+Progress" />
        <Kpi testid="kpi-completed-today" label="Completed Today" value={t.completed_today} icon={CheckCircle2} tone="ok" to="/tasks?view=completed_today" />
        <Kpi testid="kpi-clients-month" label="New Clients (Month)" value={t.clients_month} icon={Users} tone="ok" to="/clients" />
      </div>

      {billing && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi
            testid="kpi-outstanding"
            label="Outstanding"
            value={"₹" + (billing.total_outstanding / 1000).toFixed(1) + "k"}
            icon={Wallet}
            tone="danger"
            to="/billing"
          />
          <Kpi
            testid="kpi-billed-month"
            label="Billed (Month)"
            value={"₹" + (billing.billed_month / 1000).toFixed(1) + "k"}
            icon={TrendingUp}
            tone="info"
            to="/billing"
          />
          <Kpi
            testid="kpi-received-month"
            label="Received (Month)"
            value={"₹" + (billing.received_month / 1000).toFixed(1) + "k"}
            icon={CheckCircle2}
            tone="ok"
            to="/billing"
          />
          <Kpi
            testid="kpi-overdue-invoices"
            label="Overdue Invoices"
            value={billing.overdue_count}
            icon={Flame}
            tone="danger"
            to="/billing"
          />
        </div>
      )}

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Department workload</h3>
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Pending tasks</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={deptData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
              <Bar dataKey="value" fill="#2563EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Team pending</h3>
          <div className="space-y-3">
            {stats.employee_pending.length === 0 && (
              <div className="text-xs text-muted-foreground">No pending tasks.</div>
            )}
            {stats.employee_pending.map((e) => (
              <div key={e.user_id || e.name} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-blue-600 text-white grid place-items-center text-[11px] font-semibold">
                  {(e.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.name}</div>
                  <div className="h-1.5 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, e.count * 10)}%` }} />
                  </div>
                </div>
                <div className="font-mono text-sm tabular-nums">{e.count}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Upcoming deadlines</h3>
            <Link to="/tasks" className="text-xs text-blue-600 hover:underline">View all →</Link>
          </div>
          <div className="space-y-2">
            {stats.upcoming.length === 0 && (
              <div className="text-xs text-muted-foreground">Nothing due in 30 days.</div>
            )}
            {stats.upcoming.map(t => (
              <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid={`upcoming-task-${t.id}`}>
                <div className={`status-dot ${t.status === "Completed" ? "bg-emerald-500" : t.status === "In Progress" ? "bg-blue-500" : "bg-amber-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{t.department} · {t.status}</div>
                </div>
                <Badge variant="outline" className="font-mono text-[10px]">
                  {t.due_date ? format(parseISO(t.due_date), "dd MMM") : "—"}
                </Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Recent activity</h3>
          <div className="space-y-2">
            {stats.recent_activity.map(a => (
              <div key={a.id} className="flex items-start gap-3 py-1.5 text-sm">
                <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-zinc-800 grid place-items-center text-[10px] font-semibold shrink-0 mt-0.5">
                  {(a.user_name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs">
                    <span className="font-medium">{a.user_name}</span>
                    <span className="text-muted-foreground"> {a.action} </span>
                    <span className="font-mono text-[11px]">{a.entity}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {a.at ? format(parseISO(a.at), "dd MMM, HH:mm") : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
